import React from "react";

import { useResetFiles } from "@editor/components/editor/debug/useResetFiles";
import { setDebugPanelVisibility } from "@editor/reducers/core-reducer";
import { useEditorDispatch } from "@editor/store";

import NextDevServerLogs from "@/features/code-execution/CodeExecutionLogs";
import shouldShowV2Debuggers from "@/features/react-editor/utils/shouldShowV2Debuggers";
import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import { MenuItem } from "@replo/design-system/components/menu/MenuItem";
import { Modal } from "@replo/design-system/components/modal/Modal";
import {
  Bug,
  FolderTree,
  RefreshCcw,
  ScrollText,
  Wrench,
  XOctagon,
} from "lucide-react";

import { FileTreeExplorer } from "./FileTreeExplorer";

export const DebugMenu = () => {
  const [isDebugComboboxOpen, setIsDebugComboboxOpen] = React.useState(false);
  const [showLogs, setShowLogs] = React.useState(false);
  const [isFileTreeModalOpen, setIsFileTreeModalOpen] = React.useState(false);
  const [isResetFilesConfirmModalOpen, setIsResetFilesConfirmModalOpen] =
    React.useState(false);
  const dispatch = useEditorDispatch();

  const { resetFiles, isLoadingFilesReset } = useResetFiles();
  const shouldShowDebugOptions = shouldShowV2Debuggers();

  if (!shouldShowDebugOptions) {
    return null;
  }

  return (
    <Combobox.Root
      open={isDebugComboboxOpen}
      onOpenChange={setIsDebugComboboxOpen}
      onChange={(value: string) => {
        if (value === "refresh") {
          // reboot();
        } else if (value === "toggle-logs") {
          setShowLogs(!showLogs);
        } else if (value === "view-file-tree") {
          setIsFileTreeModalOpen(true);
        } else if (value === "reset-files") {
          setIsResetFilesConfirmModalOpen(true);
        } else if (value === "open-debug-panel") {
          dispatch(setDebugPanelVisibility(true));
        }
        setIsDebugComboboxOpen(false);
      }}
      options={[
        {
          value: "open-debug-panel",
          label: (
            <MenuItem variant="default" startEnhancer={<Wrench size={16} />}>
              Open Debug Panel
            </MenuItem>
          ),
          displayValue: "Open Debug Panel",
        },
        {
          value: "refresh",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<RefreshCcw size={16} />}
            >
              Refresh
            </MenuItem>
          ),
          displayValue: "Refresh",
        },
        {
          value: "toggle-logs",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<ScrollText size={16} />}
            >
              {showLogs
                ? "Hide Next Dev Server Logs"
                : "Show Next Dev Server Logs"}
            </MenuItem>
          ),
          displayValue: showLogs
            ? "Hide Next Dev Server Logs"
            : "Show Next Dev Server Logs",
        },
        {
          value: "view-file-tree",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<FolderTree size={16} />}
            >
              View File Tree
            </MenuItem>
          ),
          displayValue: "View File Tree",
        },
        {
          value: "reset-files",
          label: (
            <MenuItem
              variant="default"
              startEnhancer={<XOctagon className="text-danger" size={16} />}
              UNSAFE_className="text-danger"
            >
              Reset Files
            </MenuItem>
          ),
          displayValue: "Reset Files",
        },
      ]}
    >
      <Combobox.Trigger>
        <IconButton
          variant="tertiary"
          size="base"
          icon={<Bug className="text-muted" size={16} />}
          tooltipText="Debug Options"
        />
      </Combobox.Trigger>
      <Combobox.Popover layoutClassName="w-64">
        <Combobox.Content />
      </Combobox.Popover>
      {showLogs && <NextDevServerLogs onClose={() => setShowLogs(false)} />}
      <Modal
        isOpen={isFileTreeModalOpen}
        onOpenChange={setIsFileTreeModalOpen}
        title="WebContainer File Tree"
        size="custom"
        layoutClassName="w-screen h-screen max-w-screen max-h-screen"
      >
        <FileTreeExplorer />
      </Modal>
      <Modal
        isOpen={isResetFilesConfirmModalOpen}
        onOpenChange={setIsResetFilesConfirmModalOpen}
        title="Reset Files"
        description="This action will reset all files in the project and remove all funnels. DATA WILL BE LOST. This is not a soft-delete which can be recovered, do not click this unless you really know what you're doing and are okay with losing existing pages and funnels in this project FOREVER."
        footer={
          <div className="flex gap-2 justify-end">
            <Button
              variant="secondary"
              onClick={() => setIsResetFilesConfirmModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => void resetFiles()}
              isLoading={isLoadingFilesReset}
            >
              Reset Files
            </Button>
          </div>
        }
      />
    </Combobox.Root>
  );
};
